import { useEffect, useRef, useState } from 'react';
import { CameraScanner, type ItemNumberReading } from './CameraScanner';
import { Icon, type IconName } from './Icon';

export type ScanKind = 'lot' | 'product' | 'location' | 'bin';

const KIND: Record<ScanKind, { label: string; icon: IconName; placeholder: string }> = {
  lot: { label: 'LOT LABEL', icon: 'tag', placeholder: 'Scan or type the lot label number' },
  product: { label: 'PRODUCT BARCODE', icon: 'box', placeholder: 'Scan the product barcode' },
  location: { label: 'LOCATION', icon: 'pin', placeholder: 'Scan or type a location code' },
  bin: { label: 'BIN LABEL', icon: 'bin', placeholder: 'Scan or type the bin label (BIN…)' },
};

/**
 * One input per kind of code, colour-coded, so a number scanned into the
 * lot-label box can never be taken as a product barcode (or vice versa).
 * Works with keyboard-wedge USB/Bluetooth scanners (they type, then press Enter),
 * manual typing, and the phone camera.
 */
export function ScanField({
  kind,
  onScan,
  autoFocus,
  disabled,
  busy,
  label,
  reading,
}: {
  kind: ScanKind;
  onScan: (code: string) => void;
  autoFocus?: boolean;
  disabled?: boolean;
  busy?: boolean;
  label?: string;
  /** Product barcodes: the camera also reads the printed item number (see CameraScanner). */
  reading?: ItemNumberReading;
}) {
  const [value, setValue] = useState('');
  const [camera, setCamera] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const k = KIND[kind];

  useEffect(() => {
    if (autoFocus && !disabled) inputRef.current?.focus();
  }, [autoFocus, disabled]);

  const submit = (code: string) => {
    const c = code.trim();
    if (!c || busy) return;
    onScan(c);
    setValue('');
  };

  return (
    <div className={`scan scan-${kind}`}>
      <div className="scan-kind">
        <Icon name={k.icon} size={15} /> {label ?? k.label}
      </div>
      <form
        className="scan-row"
        onSubmit={(e) => {
          e.preventDefault();
          submit(value);
        }}
      >
        <input
          ref={inputRef}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder={k.placeholder}
          inputMode="text"
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck={false}
          disabled={disabled}
          aria-label={label ?? k.label}
        />
        <button className="btn" type="submit" disabled={disabled || busy || !value.trim()}>
          {busy ? '…' : 'Go'}
        </button>
        <button className="btn btn-ghost" type="button" disabled={disabled} onClick={() => setCamera(true)} title="Scan with camera" aria-label="Scan with camera">
          <Icon name="camera" size={20} />
        </button>
      </form>
      {camera && (
        <CameraScanner
          title={`Scan ${k.label.toLowerCase()}`}
          reading={reading}
          onResult={(code) => {
            setCamera(false);
            submit(code);
          }}
          onClose={() => setCamera(false)}
        />
      )}
    </div>
  );
}
